import type { MenuType } from '@/menus/types.ts';

type RuntimePermissionMenu = {
  code: string;
  disabled?: boolean;
  path?: string;
  public?: boolean;
  children?: RuntimePermissionMenu[];
};

/**
 * 构建运行期可用菜单树，禁用节点及其后代不进入任何运行期入口。
 */
export function filterRuntimeAvailableMenus<
  T extends { disabled?: boolean; path?: string; children?: T[] },
>(nodes: T[]): T[] {
  const result: T[] = [];

  nodes.forEach((node) => {
    if (node.disabled) return;

    const sourceChildren = node.children;
    if (!sourceChildren?.length) {
      result.push(node);
      return;
    }

    const children = filterRuntimeAvailableMenus(sourceChildren);
    if (!node.path && children.length === 0) return;

    const childrenChanged =
      children.length !== sourceChildren.length ||
      children.some((child, index) => child !== sourceChildren[index]);

    result.push(childrenChanged ? { ...node, children } : node);
  });

  return result;
}

/**
 * 处理菜单树，设置 key，检查 code 是否重复
 * @param menus 菜单数组
 * @param filter 过滤函数，接收节点作为参数，返回 true 表示保留该节点，false 表示过滤掉
 * @returns 处理后的菜单
 */
export function processMenus(
  menus: any[],
  filter?: (node: any) => boolean,
): MenuType[] {
  const codeSet = new Set<string>();
  const duplicateCodes: string[] = [];

  function dfs(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return [];
    return nodes
      .map((node) => {
        const { code, path, children, type, ...rest } = node;
        const { label = '', title } = node;

        // 如果提供了过滤函数，使用过滤函数决定是否保留节点
        if (filter && !filter(node)) {
          return null;
        }

        // 检查 code 重复
        if (code) {
          if (codeSet.has(code)) {
            duplicateCodes.push(code);
          } else {
            codeSet.add(code);
          }
        }

        const key = path ? path : code;
        const newNode: any = {
          ...rest,
          key,
          code,
          path,
          title: title || label,
          label,
        };

        // 如果原始节点有 type 字段，则保留它，默认menu
        newNode.type = type || 'menu';

        // 递归处理子节点
        if (children) {
          const processedChildren = dfs(children);
          if (processedChildren.length > 0) {
            newNode.children = processedChildren;
          }
        }
        return newNode;
      })
      .filter((node) => node !== null);
  }

  const result = dfs(menus);

  if (duplicateCodes.length > 0) {
    console.error(
      '[菜单处理] 发现重复的 code: ',
      Array.from(new Set(duplicateCodes)),
    );
  }

  return result;
}

/**
 * 收集给定 codes 的所有父级和祖先 codes
 * @param targetCodes 目标 codes 数组
 * @param menus 菜单数组
 * @returns 包含目标 codes 及其所有父级和祖先 codes 的去重数组
 */
export function collectAncestorCodes(
  targetCodes: string[],
  menus: any[],
): string[] {
  const resultCodes = new Set<string>(targetCodes);
  const targetSet = new Set(targetCodes);

  function dfs(nodes: any[], ancestors: string[]): void {
    if (!Array.isArray(nodes)) return;

    for (const node of nodes) {
      const currentPath = [...ancestors, node.code];

      // 如果当前节点的 code 在目标集合中，将所有祖先添加到结果集
      if (targetSet.has(node.code)) {
        ancestors.forEach((code) => resultCodes.add(code));
      }

      // 递归处理子节点
      if (node.children) {
        dfs(node.children, currentPath);
      }
    }
  }

  dfs(menus, []);
  return Array.from(resultCodes);
}

export function resolveMenuPermissionCodes(
  targetCodes: string[],
  menus: any[],
): string[] {
  return collectAncestorCodes(targetCodes, menus);
}

export function collectPublicMenuCodes(menus: any[]): string[] {
  const publicCodes = new Set<string>();

  function dfs(nodes: any[]): void {
    if (!Array.isArray(nodes)) return;

    nodes.forEach((node) => {
      if (node.public && node.code) {
        publicCodes.add(node.code);
      }

      if (node.children) {
        dfs(node.children);
      }
    });
  }

  dfs(menus);
  return Array.from(publicCodes);
}

export function resolveUserPermissionCodes(
  targetCodes: string[],
  menus: any[],
): string[] {
  return resolveMenuPermissionCodes(
    [...targetCodes, ...collectPublicMenuCodes(menus)],
    menus,
  );
}

export function resolveEffectiveUserPermissionCodes(
  targetCodes: string[],
  menus: any[],
): string[] {
  const descendantCodes = targetCodes.flatMap((code) =>
    collectDescendantCodes(code, menus),
  );

  return resolveUserPermissionCodes(
    [...targetCodes, ...descendantCodes],
    menus,
  );
}

/**
 * 派生运行期有效权限，同时保留不属于菜单树的兼容权限字符串。
 */
export function resolveRuntimeEffectiveUserPermissionCodes(
  targetCodes: string[],
  menus: RuntimePermissionMenu[],
): string[] {
  const runtimeMenus = filterRuntimeAvailableMenus(menus);
  const configuredCodes = new Set(collectMenuCodes(menus));
  const runtimeCodes = new Set(collectMenuCodes(runtimeMenus));
  const availableTargetCodes = targetCodes.filter(
    (code) => !configuredCodes.has(code) || runtimeCodes.has(code),
  );

  return resolveEffectiveUserPermissionCodes(
    availableTargetCodes,
    runtimeMenus,
  );
}

export function filterMenuTreePublicNodes<
  T extends { public?: boolean; children?: T[] },
>(nodes: T[], showPublic = true): T[] {
  if (showPublic) return nodes;

  return nodes
    .filter((node) => !node.public)
    .map((node) => {
      if (!node.children?.length) return node;

      return {
        ...node,
        children: filterMenuTreePublicNodes(node.children, showPublic),
      };
    });
}

export function findMenuNodeByCode<T extends { code: string; children?: T[] }>(
  nodes: T[],
  code: string,
): T | null {
  for (const node of nodes) {
    if (node.code === code) return node;
    if (!node.children?.length) continue;

    const matched = findMenuNodeByCode(node.children, code);
    if (matched) return matched;
  }

  return null;
}

export function collectMenuCodes<T extends { code: string; children?: T[] }>(
  nodes: T[],
): string[] {
  const codes: string[] = [];

  const walk = (list: T[]) => {
    list.forEach((node) => {
      codes.push(node.code);
      if (node.children?.length) walk(node.children);
    });
  };

  walk(nodes);
  return codes;
}

type MenuCodeNode = { code: string; children?: MenuCodeNode[] };

export function collectLeafMenuCodes(nodes: MenuCodeNode[]): string[] {
  return nodes.flatMap((node) =>
    node.children?.length ? collectLeafMenuCodes(node.children) : [node.code],
  );
}

/**
 * Converts historical structural assignments into the leaf-only persistence
 * shape shared by every menu permission editor.
 */
export function canonicalizeMenuAssignmentCodes(
  rawCodes: string[],
  nodes: MenuCodeNode[],
): string[] {
  const rawCodeSet = new Set(rawCodes);
  const canonicalCodes: string[] = [];

  const walk = (items: MenuCodeNode[], inherited: boolean) => {
    items.forEach((item) => {
      const selected = inherited || rawCodeSet.has(item.code);
      if (item.children?.length) {
        walk(item.children, selected);
      } else if (selected) {
        canonicalCodes.push(item.code);
      }
    });
  };

  walk(nodes, false);
  return canonicalCodes;
}

export function collectExpandableMenuCodes<
  T extends { code: string; children?: T[] },
>(nodes: T[]): string[] {
  const codes: string[] = [];

  const walk = (list: T[]) => {
    list.forEach((node) => {
      if (node.children?.length) {
        codes.push(node.code);
        walk(node.children);
      }
    });
  };

  walk(nodes);
  return codes;
}

/**
 * 收集给定 code 的所有后代codes
 * @param code
 * @param menus 菜单数组
 * @returns 所有后代 codes 的去重数组，不包括目标 code
 */
export function collectDescendantCodes(code: string, menus: any[]): string[] {
  const descendantCodes = new Set<string>();

  function collectDescendants(node: any): void {
    if (!Array.isArray(node.children)) return;
    for (const child of node.children) {
      descendantCodes.add(child.code);
      collectDescendants(child);
    }
  }

  function dfs(nodes: any[]): boolean {
    if (!Array.isArray(nodes)) return false;
    for (const node of nodes) {
      if (node.code === code) {
        collectDescendants(node);
        return true;
      }
      if (node.children && dfs(node.children)) return true;
    }
    return false;
  }

  dfs(menus);
  return Array.from(descendantCodes);
}
